import { c as _c } from "react/compiler-runtime";
import { Alert, Box, Button, HStack, Spinner, Text, VStack } from '@chakra-ui/react';
import { useColorMode } from '@src/components/chakra-ui/color-mode';
import { tokens } from '@src/theme/tokens';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { FormEvent, useEffect, useMemo, useState } from 'react';
type SessionPrechargePaymentProps = {
  clientSecret: string;
  /**
   * Stripe Customer Session for Chargebee-managed cards on the platform account.
   * Omit for Standard Connect direct charges (different Stripe account).
   */
  customerSessionClientSecret?: string | null;
  /** Required for Standard Connect direct charges. */
  stripeConnectAccountId?: string | null;
  amountUsd: number;
  /** Secondary button label. The hold is never placed until the card is authorized. */
  cancelLabel?: string;
  onAuthorized: () => void | Promise<void>;
  onCancel: () => void | Promise<void>;
};
const publishableKey = process.env.NEXT_PUBLIC_STRIPE_PUBLISHED_KEY;
const SessionPrechargePaymentInner = (props: SessionPrechargePaymentProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);
    if (!stripe || !elements) {
      setErrorMessage('Payment form is still loading. Try again in a moment.');
      return;
    }
    setProcessing(true);
    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          // Manual capture PIs finish without redirect in normal card flows.
          return_url: typeof window !== 'undefined' ? window.location.href : 'https://www.startups.com'
        },
        redirect: 'if_required'
      });
      if (result.error) {
        setErrorMessage(result.error.message || 'Card authorization failed.');
        setProcessing(false);
        return;
      }
      const status = result.paymentIntent?.status;
      if (status === 'requires_capture' || status === 'succeeded' || status === 'processing') {
        await props.onAuthorized();
        return;
      }
      setErrorMessage(`Unexpected payment status: ${status || 'unknown'}. Try another card.`);
      setProcessing(false);
    } catch (err: any) {
      setErrorMessage(err?.message || 'Could not authorize payment.');
      setProcessing(false);
    }
  };
  const handleCancel = async () => {
    setCanceling(true);
    try {
      await props.onCancel();
    } finally {
      setCanceling(false);
    }
  };
  return <VStack align={'stretch'} as={'form'} gap={4} onSubmit={handleSubmit}>
      <Text color={'colorPalette.fg/70'} fontFamily={'longform'} fontSize={'sm'}>
        Hold{' '}
        <Text as={'span'} color={'colorPalette.fg'} fontWeight={'semibold'}>
          ${Number(props.amountUsd).toFixed(2)}
        </Text>{' '}
        on a card. Your request goes to the advisor once the hold is in place. You’re charged this full
        package when the advisor confirms a time. Cancel before anyone joins for a full refund.
        {props.customerSessionClientSecret ? ' Cards already on your membership billing are listed here. A new card is used for this session only — it is not saved to membership billing.' : null}
      </Text>

      {errorMessage && <Alert.Root borderRadius={'md'} status={'error'}>
          <Alert.Indicator />
          <Box ml={2}>{errorMessage}</Box>
        </Alert.Root>}

      <PaymentElement options={{
      layout: 'auto',
      paymentMethodOrder: ['card']
    }} />

      <HStack gap={3} wrap={'wrap'}>
        <Button disabled={!stripe || canceling} loading={processing} type={'submit'} variant={'cta'}>
          Authorize card
        </Button>
        <Button disabled={processing} loading={canceling} type={'button'} variant={'outline'} onClick={() => void handleCancel()}>
          {props.cancelLabel || 'Cancel request'}
        </Button>
      </HStack>
    </VStack>;
};

/**
 * Stripe Elements step of pay-first booking (L13): confirms the manual-capture
 * PaymentIntent from `prepareAdvisorSessionPayment` (3DS handled by Stripe.js).
 * Standard Connect direct charges need stripeConnectAccountId on loadStripe.
 */
export const SessionPrechargePayment = props => {
  const $ = _c(25);
  const {
    colorMode
  } = useColorMode();
  const isDarkMode = colorMode === "dark";
  const colors = tokens.tokens.colors;
  let t0;
  if ($[0] !== props.stripeConnectAccountId) {
    bb0: {
      if (!publishableKey) {
        t0 = null;
        break bb0;
      }
      const accountId = props.stripeConnectAccountId?.trim();
      if (accountId) {
        t0 = loadStripe(publishableKey, {
          stripeAccount: accountId
        });
        break bb0;
      }
      let t1;
      if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = loadStripe(publishableKey);
        $[2] = t1;
      } else {
        t1 = $[2];
      }
      t0 = t1;
    }
    $[0] = props.stripeConnectAccountId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const stripePromise = t0;
  const [stripe, setStripe] = useState(null);
  let t1;
  let t2;
  if ($[3] !== stripePromise) {
    t1 = () => {
      let cancelled = false;
      if (!stripePromise) {
        setStripe(null);
        return;
      }
      stripePromise.then(s => {
        if (!cancelled) {
          setStripe(s);
        }
      });
      return () => {
        cancelled = true;
      };
    };
    t2 = [stripePromise];
    $[3] = stripePromise;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  if (!publishableKey) {
    let t3;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Alert.Root borderRadius="md" status="error"><Alert.Indicator /><Box ml={2}>Stripe is not configured on this front-end (NEXT_PUBLIC_STRIPE_PUBLISHED_KEY).</Box></Alert.Root>;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  if (!stripe || !props.clientSecret) {
    let t3;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <HStack gap={3} justify="center" py={6}><Spinner size="sm" /><Text color="colorPalette.fg/60" fontSize="sm">Loading payment form…</Text></HStack>;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    return t3;
  }
  let t3;
  if ($[8] !== props.customerSessionClientSecret) {
    t3 = props.customerSessionClientSecret ? {
      customerSessionClientSecret: props.customerSessionClientSecret
    } : {};
    $[8] = props.customerSessionClientSecret;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const t4 = isDarkMode ? colors.purple["900"].value : colors.orange.card;
  const t5 = isDarkMode ? colors.purple["300"].value : colors.orange["600"].value;
  const t6 = isDarkMode ? colors.gray["100"].value : colors.gray["900"].value;
  const t7 = isDarkMode ? colors.gray["200"].value : colors.gray["900"].value;
  let t8;
  if ($[10] !== t4 || $[11] !== t5 || $[12] !== t6 || $[13] !== t7) {
    t8 = {
      theme: "stripe",
      variables: {
        borderRadius: "6px",
        colorBackground: t4,
        colorDanger: colors.red["500"].value,
        colorPrimary: t5,
        colorSuccess: colors.green["500"].value,
        colorText: t6,
        colorTextPlaceholder: t7,
        fontFamily: "Lato, system-ui, sans-serif",
        spacingUnit: "4px",
        gridRowSpacing: "16px",
        fontSizeBase: "16px"
      }
    };
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== props.clientSecret || $[16] !== t3 || $[17] !== t8) {
    t9 = {
      clientSecret: props.clientSecret,
      ...t3,
      appearance: t8
    };
    $[15] = props.clientSecret;
    $[16] = t3;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== props) {
    t10 = <SessionPrechargePaymentInner {...props} />;
    $[19] = props;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== stripe || $[22] !== t10 || $[23] !== t9) {
    t11 = <Elements stripe={stripe} options={t9}>{t10}</Elements>;
    $[21] = stripe;
    $[22] = t10;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  return t11;
};